import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RollbackOutlined } from '@ant-design/icons';
import { canUseInstitutionSwitcher, useAuth } from '../../auth/AuthContext';
import {
  cancelWorkspaceQueries,
  clearWorkspaceCaches,
} from '../../auth/workspaceCaches';
import './InstitutionContextBanner.css';

const formatAssumedRoleLabel = (role: string): string =>
  role
    .split('-')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatInstitutionLabel = (
  institutionName: string | null,
  institutionId: string | null,
): string => institutionName?.trim() || institutionId || 'selected institution';

export const InstitutionContextBanner: React.FC = () => {
  const {
    originalUser,
    user,
    assumedRole,
    assumedInstitutionId,
    assumedInstitutionName,
    resetAssumedRole,
    institutionSwitcherEnabled,
  } = useAuth();
  const navigate = useNavigate();

  if (
    !canUseInstitutionSwitcher(originalUser ?? user, institutionSwitcherEnabled) ||
    !assumedRole ||
    !assumedInstitutionId
  ) {
    return null;
  }

  const roleLabel = formatAssumedRoleLabel(assumedRole);
  const institutionLabel = formatInstitutionLabel(
    assumedInstitutionName,
    assumedInstitutionId,
  );

  return (
    <section className="institution-context-banner" role="status" aria-live="polite">
      <div className="institution-context-banner__content">
        <span className="institution-context-banner__eyebrow">
          Assumed institution context active
        </span>
        <h2 className="institution-context-banner__title">
          {roleLabel} at {institutionLabel}
        </h2>
        <p className="institution-context-banner__description">
          Platform-only features are hidden while this temporary institution context is
          active.
        </p>
      </div>

      <div className="institution-context-banner__actions">
        <button
          type="button"
          className="institution-context-banner__button institution-context-banner__button--primary"
          onClick={async () => {
            await cancelWorkspaceQueries();
            resetAssumedRole();
            clearWorkspaceCaches();
            navigate('/', { replace: true });
          }}
        >
          <RollbackOutlined />
          <span>Return to platform</span>
        </button>
      </div>
    </section>
  );
};

export default InstitutionContextBanner;
